/* eslint-disable react/no-danger */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  AlignCenterOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
  BoldOutlined,
  ClearOutlined,
  CodeOutlined,
  EyeOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  ItalicOutlined,
  LinkOutlined,
  MinusOutlined,
  OrderedListOutlined,
  PictureOutlined,
  RedoOutlined,
  SplitCellsOutlined,
  StrikethroughOutlined,
  UnderlineOutlined,
  UndoOutlined,
  UnorderedListOutlined
} from '@ant-design/icons'
import { Input, Modal, Tooltip } from 'antd'
import PropTypes from 'prop-types'

import styles from './RichTextEditor.module.css'

const HEADINGS = [
  { label: 'Paragraph', value: 'p' },
  { label: 'Heading 1', value: 'h1' },
  { label: 'Heading 2', value: 'h2' },
  { label: 'Heading 3', value: 'h3' },
  { label: 'Heading 4', value: 'h4' }
]

const FONT_SIZES = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px']

/** execCommand foreColor/hiliteColor emit deprecated <font> tags; unwrap to spans. */
const unwrapFontTags = root => {
  if (!root) return
  let fontEl = root.querySelector('font')
  while (fontEl) {
    const span = document.createElement('span')
    const color = fontEl.getAttribute('color')
    const face = fontEl.getAttribute('face')
    const size = fontEl.getAttribute('size')
    if (color) span.style.color = color
    // Preserve legacy font face/size from pasted Office/Salesforce HTML.
    if (face) span.style.fontFamily = face
    if (size && !Number.isNaN(Number(size))) {
      const legacySizeToPx = {
        1: '10px',
        2: '13px',
        3: '16px',
        4: '18px',
        5: '24px',
        6: '32px',
        7: '48px'
      }
      span.style.fontSize = legacySizeToPx[Number(size)] || span.style.fontSize
    }
    if (fontEl.style.cssText) span.style.cssText = fontEl.style.cssText
    while (fontEl.firstChild) span.appendChild(fontEl.firstChild)
    fontEl.replaceWith(span)
    fontEl = root.querySelector('font')
  }
}

const normalizeHtmlFonts = html => {
  if (!html || !/<font\b/i.test(html)) return html
  const el = document.createElement('div')
  el.innerHTML = html
  unwrapFontTags(el)
  return el.innerHTML
}

const stripInlineColorStyles = html => {
  if (!html) return html
  const container = document.createElement('div')
  container.innerHTML = html

  const allElements = container.querySelectorAll('*')
  allElements.forEach((element) => {
    if (element.hasAttribute('color')) {
      element.removeAttribute('color')
    }
    if (element.hasAttribute('bgcolor')) {
      element.removeAttribute('bgcolor')
    }
    if (element.style) {
      element.style.removeProperty('color')
      element.style.removeProperty('background-color')
      element.style.removeProperty('background')
    }
  })

  // Convert Word/Salesforce "fake lists" (MsoListParagraph) into semantic ul/li.
  const allBlocks = Array.from(container.querySelectorAll('p, div'))
  let activeList = null
  allBlocks.forEach((block) => {
    const styleAttr = (block.getAttribute('style') || '').toLowerCase()
    const classAttr = (block.getAttribute('class') || '').toLowerCase()
    const isMsoList = styleAttr.includes('mso-list') || classAttr.includes('msolistparagraph')

    if (!isMsoList) {
      activeList = null
      return
    }

    if (!activeList) {
      activeList = document.createElement('ul')
      activeList.style.margin = '0 0 1em'
      activeList.style.paddingLeft = '1.5em'
      block.parentNode.insertBefore(activeList, block)
    }

    const listItem = document.createElement('li')
    let itemHtml = block.innerHTML
      // Remove Office "ignore" marker spans that contain bullet/number glyphs.
      .replace(/<span[^>]*mso-list:\s*ignore[^>]*>[\s\S]*?<\/span>/gi, '')
      // Remove leading bullet glyphs that may remain in copied HTML.
      .replace(/^\s*([•·▪◦o]\s*)+/i, '')
      // Remove leading numbered marker if it was plain text.
      .replace(/^\s*\d+[.)]\s+/, '')

    if (!itemHtml.trim()) {
      const rawText = (block.textContent || '')
        .replace(/^\s*([•·▪◦o]\s*)+/i, '')
        .replace(/^\s*\d+[.)]\s+/, '')
        .trim()
      itemHtml = rawText
    }

    listItem.innerHTML = itemHtml
    activeList.appendChild(listItem)
    block.remove()
  })

  return container.innerHTML
}

const COLORS = [
  '#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc', '#d9d9d9', '#efefef', '#f3f3f3', '#ffffff',
  '#980000', '#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#4a86e8', '#0000ff', '#9900ff', '#ff00ff',
  '#e6b8af', '#f4cccc', '#fce5cd', '#fff2cc', '#d9ead3', '#d0e0e3', '#c9daf8', '#cfe2f3', '#d9d2e9', '#ead1dc'
]

const Divider = () => <div className={styles.toolbarDivider} />

/**
 * Using onMouseDown + preventDefault keeps the editor focused (preserving the
 * active selection) so execCommand has a valid range to act on.
 */
const ToolbarButton = ({ icon, title, onClick, active, disabled: dis }) => (
  <Tooltip title={title}>
    <button
      className={`${styles.toolbarBtn}${active ? ` ${styles.active}` : ''}`}
      disabled={dis}
      onMouseDown={e => { e.preventDefault(); if (!dis) onClick() }}
      type="button"
    >
      {icon}
    </button>
  </Tooltip>
)

ToolbarButton.propTypes = {
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  icon: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired
}

const RichTextEditor = ({
  disabled,
  minHeight,
  onChange,
  placeholder,
  value
}) => {
  const editorRef = useRef(null)
  const colorAnchorRef = useRef(null)
  const bgColorAnchorRef = useRef(null)
  const savedSelectionRef = useRef(null)

  const [fullscreen, setFullscreen] = useState(false)
  const [viewMode, setViewMode] = useState('edit')
  const [heading, setHeading] = useState('p')
  const [fontSize, setFontSize] = useState('14px')
  const [content, setContent] = useState(value || '')
  const [colorPopover, setColorPopover] = useState(null)
  const [linkModal, setLinkModal] = useState({ open: false, url: '', text: '' })
  const [imageModal, setImageModal] = useState({ open: false, url: '', alt: '' })
  const [wordCount, setWordCount] = useState(0)

  useEffect(() => {
    const normalized = normalizeHtmlFonts(value || '')
    if (editorRef.current && value !== undefined) {
      if (editorRef.current.innerHTML !== normalized) {
        editorRef.current.innerHTML = normalized
      }
    }
    setContent(normalized)
  }, [value])

  const saveSelection = useCallback(() => {
    const sel = window.getSelection()
    if (sel && sel.rangeCount > 0) {
      savedSelectionRef.current = sel.getRangeAt(0).cloneRange()
    }
  }, [])

  const restoreSelection = useCallback(() => {
    const saved = savedSelectionRef.current
    if (!saved) return
    editorRef.current?.focus()
    const sel = window.getSelection()
    if (sel) {
      sel.removeAllRanges()
      sel.addRange(saved)
    }
  }, [])

  const handleContentChange = useCallback(() => {
    if (editorRef.current) {
      unwrapFontTags(editorRef.current)
      const html = editorRef.current.innerHTML
      const text = editorRef.current.innerText || ''
      setContent(html)
      setWordCount(text.trim() ? text.trim().split(/\s+/).length : 0)
      if (onChange) onChange(html)
    }
  }, [onChange])

  const applyColor = useCallback((type, color) => {
    restoreSelection()
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0) return
    const range = sel.getRangeAt(0)
    if (range.collapsed) return

    const span = document.createElement('span')
    if (type === 'text') span.style.color = color
    else span.style.backgroundColor = color

    try {
      range.surroundContents(span)
    } catch {
      const contents = range.extractContents()
      span.appendChild(contents)
      range.insertNode(span)
    }
    handleContentChange()
  }, [handleContentChange, restoreSelection])

  const exec = useCallback((cmd, val) => {
    restoreSelection()
    document.execCommand(cmd, false, val)
    handleContentChange()
  }, [handleContentChange, restoreSelection])

  const handleHeadingChange = e => {
    const tag = e.target.value
    setHeading(tag)
    restoreSelection()
    document.execCommand('formatBlock', false, `<${tag}>`)
    handleContentChange()
  }

  const handleFontSizeChange = e => {
    const size = e.target.value
    setFontSize(size)
    restoreSelection()
    const sel = window.getSelection()
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0)
      const span = document.createElement('span')
      span.style.fontSize = size
      try { range.surroundContents(span) } catch (_) { /* cross-node selection */ }
      handleContentChange()
    }
  }

  const insertLink = () => {
    setLinkModal({ open: false, url: '', text: '' })
    if (linkModal.url) {
      const html = `<a href="${linkModal.url}" target="_blank" rel="noopener noreferrer">${linkModal.text || linkModal.url}</a>`
      restoreSelection()
      document.execCommand('insertHTML', false, html)
      handleContentChange()
    }
  }

  const insertImage = () => {
    setImageModal({ open: false, url: '', alt: '' })
    if (imageModal.url) {
      const html = `<img src="${imageModal.url}" alt="${imageModal.alt}" style="max-width:100%;height:auto;" />`
      restoreSelection()
      document.execCommand('insertHTML', false, html)
      handleContentChange()
    }
  }

  const insertTable = () => {
    const html = `<table style="border-collapse:collapse;width:100%;margin:8px 0">
      <tr><td style="border:1px solid #d9d9d9;padding:6px">&nbsp;</td><td style="border:1px solid #d9d9d9;padding:6px">&nbsp;</td><td style="border:1px solid #d9d9d9;padding:6px">&nbsp;</td></tr>
      <tr><td style="border:1px solid #d9d9d9;padding:6px">&nbsp;</td><td style="border:1px solid #d9d9d9;padding:6px">&nbsp;</td><td style="border:1px solid #d9d9d9;padding:6px">&nbsp;</td></tr>
    </table>`
    restoreSelection()
    document.execCommand('insertHTML', false, html)
    handleContentChange()
  }

  const isDisabled = disabled || viewMode === 'preview'

  const handleKeyDown = useCallback(e => {
    if (e.key === 'Enter') {
      const sel = window.getSelection()
      if (!sel || sel.rangeCount === 0) return
      // Let the browser handle Enter inside lists (new list item / exit list)
      let node = sel.getRangeAt(0).commonAncestorContainer
      while (node && node !== editorRef.current) {
        if (node.nodeName === 'LI' || node.nodeName === 'UL' || node.nodeName === 'OL') return
        node = node.parentNode
      }
      // Outside a list — insert <br> instead of <div>
      e.preventDefault()
      document.execCommand('insertLineBreak')
    }
  }, [])

  const handlePaste = useCallback((event) => {
    if (isDisabled) {
      return
    }

    event.preventDefault()
    const clipboardData = event.clipboardData || window.clipboardData
    const html = clipboardData?.getData('text/html')
    const plainText = clipboardData?.getData('text/plain')

    if (html) {
      const sanitizedHtml = stripInlineColorStyles(normalizeHtmlFonts(html))
      document.execCommand('insertHTML', false, sanitizedHtml)
      handleContentChange()
      return
    }

    if (plainText) {
      document.execCommand('insertText', false, plainText)
      handleContentChange()
    }
  }, [handleContentChange, isDisabled])

  const ColorPopover = ({ type }) => (
    <div className={styles.colorPopover}>
      <div style={{ color: '#8c8c8c', fontSize: 11, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase' }}>
        {type === 'text' ? 'Text colour' : 'Highlight'}
      </div>
      <div className={styles.colorGrid}>
        {COLORS.map(c => (
          <div
            key={c}
            className={styles.colorSwatch}
            onMouseDown={e => {
              e.preventDefault()
              applyColor(type === 'text' ? 'text' : 'bg', c)
              setColorPopover(null)
            }}
            style={{ backgroundColor: c, borderColor: c === '#ffffff' ? '#d9d9d9' : 'transparent' }}
          />
        ))}
      </div>
    </div>
  )

  ColorPopover.propTypes = { type: PropTypes.string.isRequired }

  return (
    <div className={`${styles.wrapper}${fullscreen ? ` ${styles.fullscreen}` : ''}`}>
      {/* ── Toolbar ── */}
      <div className={styles.toolbar}>

        {/* View toggle */}
        <div className={styles.viewToggle}>
          {['edit', 'split', 'preview'].map(mode => (
            <button
              key={mode}
              className={`${styles.viewToggleBtn}${viewMode === mode ? ` ${styles.viewActive}` : ''}`}
              onClick={() => setViewMode(mode)}
              type="button"
            >
              {mode === 'edit' && <><EyeOutlined style={{ marginRight: 3 }} />Edit</>}
              {mode === 'split' && <><SplitCellsOutlined style={{ marginRight: 3 }} />Split</>}
              {mode === 'preview' && <><EyeOutlined style={{ marginRight: 3 }} />Preview</>}
            </button>
          ))}
        </div>

        <Divider />

        {/* Undo / Redo */}
        <div className={styles.toolbarGroup}>
          <ToolbarButton disabled={isDisabled} icon={<UndoOutlined />} onClick={() => exec('undo')} title="Undo (Ctrl+Z)" />
          <ToolbarButton disabled={isDisabled} icon={<RedoOutlined />} onClick={() => exec('redo')} title="Redo (Ctrl+Y)" />
        </div>

        <Divider />

        {/* Heading + font size */}
        <div className={styles.toolbarGroup}>
          <select className={styles.toolbarSelect} disabled={isDisabled} onChange={handleHeadingChange} value={heading}>
            {HEADINGS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
          </select>
          <select className={styles.toolbarSelect} disabled={isDisabled} onChange={handleFontSizeChange} value={fontSize}>
            {FONT_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <Divider />

        {/* Inline formatting */}
        <div className={styles.toolbarGroup}>
          <ToolbarButton disabled={isDisabled} icon={<BoldOutlined />} onClick={() => exec('bold')} title="Bold (Ctrl+B)" />
          <ToolbarButton disabled={isDisabled} icon={<ItalicOutlined />} onClick={() => exec('italic')} title="Italic (Ctrl+I)" />
          <ToolbarButton disabled={isDisabled} icon={<UnderlineOutlined />} onClick={() => exec('underline')} title="Underline (Ctrl+U)" />
          <ToolbarButton disabled={isDisabled} icon={<StrikethroughOutlined />} onClick={() => exec('strikethrough')} title="Strikethrough" />
        </div>

        <Divider />

        {/* Colour */}
        <div className={styles.toolbarGroup} style={{ position: 'relative' }}>
          <Tooltip title="Text colour">
            <button
              ref={colorAnchorRef}
              className={styles.toolbarBtn}
              disabled={isDisabled}
              onMouseDown={e => { e.preventDefault(); if (!isDisabled) setColorPopover(colorPopover === 'text' ? null : 'text') }}
              type="button"
            >
              <span style={{ borderBottom: '3px solid #1677ff', lineHeight: 1 }}>A</span>
            </button>
          </Tooltip>
          <Tooltip title="Highlight">
            <button
              ref={bgColorAnchorRef}
              className={styles.toolbarBtn}
              disabled={isDisabled}
              onMouseDown={e => { e.preventDefault(); if (!isDisabled) setColorPopover(colorPopover === 'bg' ? null : 'bg') }}
              type="button"
            >
              <span style={{ backgroundColor: '#ffff00', padding: '0 3px' }}>H</span>
            </button>
          </Tooltip>
          {colorPopover && (
            <div style={{ left: 0, position: 'absolute', top: 30, zIndex: 1050 }}>
              <ColorPopover type={colorPopover === 'text' ? 'text' : 'bg'} />
            </div>
          )}
        </div>

        <Divider />

        {/* Alignment */}
        <div className={styles.toolbarGroup}>
          <ToolbarButton disabled={isDisabled} icon={<AlignLeftOutlined />} onClick={() => exec('justifyLeft')} title="Align left" />
          <ToolbarButton disabled={isDisabled} icon={<AlignCenterOutlined />} onClick={() => exec('justifyCenter')} title="Align center" />
          <ToolbarButton disabled={isDisabled} icon={<AlignRightOutlined />} onClick={() => exec('justifyRight')} title="Align right" />
        </div>

        <Divider />

        {/* Lists */}
        <div className={styles.toolbarGroup}>
          <ToolbarButton disabled={isDisabled} icon={<UnorderedListOutlined />} onClick={() => exec('insertUnorderedList')} title="Bullet list" />
          <ToolbarButton disabled={isDisabled} icon={<OrderedListOutlined />} onClick={() => exec('insertOrderedList')} title="Numbered list" />
        </div>

        <Divider />

        {/* Insert */}
        <div className={styles.toolbarGroup}>
          <ToolbarButton
            disabled={isDisabled}
            icon={<LinkOutlined />}
            onClick={() => {
              saveSelection()
              const sel = window.getSelection()
              setLinkModal({ open: true, url: '', text: sel?.toString() || '' })
            }}
            title="Insert link"
          />
          <ToolbarButton
            disabled={isDisabled}
            icon={<PictureOutlined />}
            onClick={() => { saveSelection(); setImageModal({ open: true, url: '', alt: '' }) }}
            title="Insert image URL"
          />
          <ToolbarButton disabled={isDisabled} icon={<SplitCellsOutlined />} onClick={insertTable} title="Insert table" />
          <ToolbarButton disabled={isDisabled} icon={<MinusOutlined />} onClick={() => exec('insertHorizontalRule')} title="Horizontal rule" />
          <ToolbarButton disabled={isDisabled} icon={<CodeOutlined />} onClick={() => exec('formatBlock', '<pre>')} title="Code block" />
        </div>

        <Divider />

        {/* Clear + fullscreen */}
        <div className={styles.toolbarGroup}>
          <ToolbarButton disabled={isDisabled} icon={<ClearOutlined />} onClick={() => exec('removeFormat')} title="Clear formatting" />
          <ToolbarButton
            icon={fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            onClick={() => setFullscreen(f => !f)}
            title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          />
        </div>
      </div>

      {/* ── Content area ── */}
      <div className={styles.contentArea}>
        {/*
          Keep the contenteditable mounted in preview mode (hidden) so innerHTML is not
          lost when switching back to edit. Sync-from-`value` only runs in useEffect when
          `value` changes; remounting the editor after preview left it empty before.
        */}
        <div
          ref={editorRef}
          className={styles.editor}
          contentEditable={!disabled && viewMode !== 'preview'}
          data-placeholder={placeholder}
          onBlur={() => { saveSelection(); handleContentChange() }}
          onInput={handleContentChange}
          onKeyDown={handleKeyDown}
          onKeyUp={saveSelection}
          onMouseUp={saveSelection}
          onPaste={handlePaste}
          style={{
            borderRight: viewMode === 'split' ? '1px solid #e8e8e8' : 'none',
            display: viewMode === 'preview' ? 'none' : undefined,
            minHeight
          }}
          suppressContentEditableWarning
        />
        {(viewMode === 'preview' || viewMode === 'split') && (
          <div style={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
            {viewMode === 'split' && <div className={styles.previewLabel}>Preview</div>}
            <div
              className={styles.preview}
              dangerouslySetInnerHTML={{ __html: content || `<span style="color:#bfbfbf">${placeholder}</span>` }}
              style={{ minHeight }}
            />
          </div>
        )}
      </div>

      {/* ── Status bar ── */}
      <div className={styles.statusBar}>
        <span>{wordCount} word{wordCount !== 1 ? 's' : ''}</span>
      </div>

      {/* ── Link modal ── */}
      <Modal
        okButtonProps={{ disabled: !linkModal.url }}
        okText="Insert"
        onCancel={() => setLinkModal({ open: false, url: '', text: '' })}
        onOk={insertLink}
        open={linkModal.open}
        title="Insert link"
        width={400}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 8 }}>
          <Input
            onChange={e => setLinkModal(s => ({ ...s, url: e.target.value }))}
            placeholder="https://example.com"
            prefix={<LinkOutlined style={{ color: '#bfbfbf' }} />}
            value={linkModal.url}
          />
          <Input
            onChange={e => setLinkModal(s => ({ ...s, text: e.target.value }))}
            placeholder="Link text (optional)"
            value={linkModal.text}
          />
        </div>
      </Modal>

      {/* ── Image modal ── */}
      <Modal
        okButtonProps={{ disabled: !imageModal.url }}
        okText="Insert"
        onCancel={() => setImageModal({ open: false, url: '', alt: '' })}
        onOk={insertImage}
        open={imageModal.open}
        title="Insert image"
        width={400}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 8 }}>
          <Input
            onChange={e => setImageModal(s => ({ ...s, url: e.target.value }))}
            placeholder="https://example.com/image.jpg"
            prefix={<PictureOutlined style={{ color: '#bfbfbf' }} />}
            value={imageModal.url}
          />
          <Input
            onChange={e => setImageModal(s => ({ ...s, alt: e.target.value }))}
            placeholder="Alt text (optional)"
            value={imageModal.alt}
          />
        </div>
      </Modal>
    </div>
  )
}

RichTextEditor.defaultProps = {
  disabled: false,
  minHeight: 180,
  placeholder: 'Start typing…',
  value: ''
}

RichTextEditor.propTypes = {
  disabled: PropTypes.bool,
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  value: PropTypes.string
}

export default RichTextEditor
